import { Component, NgZone, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { combineLatest, fromEvent, Observable, Subscription } from 'rxjs';
import { debounceTime, delay, filter } from 'rxjs/operators';
import { Idle, DocumentInterruptSource } from '@ng-idle/core';
import { OAuthService, OAuthErrorEvent } from 'angular-oauth2-oidc';
import { DateTime, Duration } from 'luxon';
import { ConfigService } from './services/config.service';
import { AuthenticationService } from './authentication/services/authentication.service';
import { SnackbarService } from './services/snackbar.sevice';
import { User } from './models/user';
import { Constants } from './models/constants';
import { OnlineStatusHandler } from './services/online.status.handler';
import { GetClient } from './api-module/control-centre-api-client';
import { PendoService } from './services/pendo/pendo-service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnDestroy {
    private static readonly WindowResizeTrackingDebounceDuration = 30000; // 30 seconds
    private readonly subscriptions = new Subscription();
    private readonly idleHandlerSubscriptions = new Subscription();

    title = 'Control Centre';
    copyrightYear = new Date().getFullYear();

    user$: Observable<User>;
    client: GetClient;

    activeChildComponent: any;

    idleTimeoutCountdownSeconds = 0;
    idleTimeoutCountdownString = '';
    private readonly IDLE_ACTION_SECONDS: number = 1800; // 30 minutes
    private readonly TIMEOUT_COUNTDOWN_SECONDS: number = 300; // 5 minutes
    private sessionTimeoutTime: DateTime;

    constructor(
        private readonly router: Router,
        private readonly authenticationService: AuthenticationService,
        private readonly oAuthService: OAuthService,
        private readonly snackBarService: SnackbarService,
        private readonly iconRegistry: MatIconRegistry,
        private readonly sanitizer: DomSanitizer,
        private readonly ngZone: NgZone,
        private readonly idleHandler: Idle,
        private readonly onlineStatusHandler: OnlineStatusHandler,
        private readonly pendoService: PendoService
    ) {
        if (ConfigService.settings.uiIdleHandler) {
            this.IDLE_ACTION_SECONDS = ConfigService.settings.uiIdleHandler.idleActionSeconds;
            this.TIMEOUT_COUNTDOWN_SECONDS = ConfigService.settings.uiIdleHandler.timeoutCountdownSeconds;
        }

        this.registerIcons();

        this.subscribeCurrentUser();

        this.subscribeOAuthEvents();

        this.subscribeScreenResize();

        this.configureGoogleAnalytics(router);

        this.subscribeOnlineStatus();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    onActivate(event: any) {
        this.activeChildComponent = event;
    }

    login() {
        this.router.navigate(['/login']);
    }

    logout() {
        this.authenticationService.doLogout();
    }

    idleLogout() {
        this.pendoService.trackEvent({
            Type: 'IdleLogout',
            Body: {
                AppName: 'ControlCentreApp'
            }
        });

        this.authenticationService.doLogout();
    }

    private subscribeOAuthEvents() {
        this.subscriptions.add(
            combineLatest([this.user$, this.oAuthService.events]).subscribe(([user, event]) => {
                const that = this;
                const normalEventTypes = [
                    'token_received',
                    'discovery_document_loaded',
                    'token_expires',
                    'token_refreshed',
                    'logout',
                    'code_error'
                ];
                if (!normalEventTypes.some(type => type === event.type)) {
                    console.error(`${event.type}. Details: ${JSON.stringify(event)}`);
                }

                switch (event.type) {
                    case 'discovery_document_load_error':
                    case 'jwks_load_error':
                    case 'discovery_document_validation_error':
                    case 'token_refresh_error':
                        that.snackBarService.openSnackBar(Constants.Auth0GenericErrorMessage, SnackbarService.SNACK_BAR_CLASS_ALERT);
                        break;

                    case 'code_error': {
                        const errorEvent = event as OAuthErrorEvent;
                        const errorKey = 'error';
                        if (errorEvent.params[errorKey] === 'login_required') {
                            if (user) {
                                that.logout();
                            }
                        } else if (errorEvent.params[errorKey] === 'unauthorized' || errorEvent.params[errorKey] === 'access_denied') {
                            that.snackBarService.openSnackBar(Constants.UserBlockedMessage, SnackbarService.SNACK_BAR_CLASS_ALERT);
                        } else {
                            const message =
                                'Got error response from auth server.' +
                                ' Reason: ' +
                                errorEvent.reason +
                                ', Error: ' +
                                errorEvent.params[errorKey];

                            that.snackBarService.openSnackBar(message, SnackbarService.SNACK_BAR_CLASS_ALERT);
                        }

                        break;
                    }

                    case 'logout':
                        this.closeIdleHandler();
                        break;
                }
            })
        );
    }

    private subscribeCurrentUser() {
        // delay(0) is used to fix Expression has changed after it was checked
        this.user$ = this.authenticationService.currentUser.pipe(delay(0));

        this.subscriptions.add(
            this.user$.subscribe(user => {
                if (!user) {
                    return;
                }

                if (user.orgs && user.orgs.size > 0) {
                    this.client = user.orgs.values().next().value;
                }

                if (this.initIdleHandler()) {
                    this.startIdleHandler();
                }
            })
        );
    }

    private subscribeScreenResize() {
        this.subscriptions.add(
            fromEvent(window, 'resize')
                .pipe(debounceTime(AppComponent.WindowResizeTrackingDebounceDuration))
                .subscribe(() =>
                    this.pendoService.trackEvent({
                        Type: 'ScreenResize',
                        Body: {
                            AppName: 'ControlCentreApp',
                            ScreenWidth: JSON.stringify(screen.width),
                            ScreenHeight: JSON.stringify(screen.height),
                            WindowWidth: JSON.stringify(window.innerWidth),
                            WindowHeight: JSON.stringify(window.innerHeight)
                        }
                    })
                )
        );
    }

    private registerIcons() {
        this.iconRegistry.addSvgIcon('confirmation', this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/confirmation.svg'));
        this.iconRegistry.addSvgIcon('information', this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/information.svg'));
        this.iconRegistry.addSvgIcon('warning', this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/warning.svg'));
        this.iconRegistry.addSvgIcon(
            'warning-triangle',
            this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/warning-triangle.svg')
        );
        this.iconRegistry.addSvgIcon(
            'filled-warning-triangle',
            this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/filled-warning-triangle.svg')
        );
        this.iconRegistry.addSvgIcon('warning-main', this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/warning-main.svg'));
        this.iconRegistry.addSvgIcon('dataset_linked', this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/dataset_linked.svg'));
        this.iconRegistry.addSvgIcon('green-checkmark', this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/green-checkmark.svg'));
        this.iconRegistry.addSvgIcon('grey-checkmark', this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/CheckCirclegrey.svg'));
        this.iconRegistry.addSvgIcon('white-cross', this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/white-cross.svg'));
        this.iconRegistry.addSvgIcon('red-cross', this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/red-cross.svg'));
        this.iconRegistry.addSvgIcon('grey-cross', this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/XCirclegrey.svg'));
        this.iconRegistry.addSvgIcon('spinner', this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/spinner.svg'));
        this.iconRegistry.addSvgIcon('sliders', this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/sliders.svg'));
        this.iconRegistry.addSvgIcon('cancel', this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/X.svg'));
        this.iconRegistry.addSvgIcon('edit', this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/pencil-simple.svg'));
        this.iconRegistry.addSvgIcon('save', this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/floppy-disk.svg'));
        this.iconRegistry.addSvgIcon('upload', this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/upload-simple.svg'));
        this.iconRegistry.addSvgIcon(
            'locking-full-lock',
            this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/locking-locked.svg')
        );
    }

    private startIdleHandler() {
        this.idleTimeoutCountdownSeconds = 0;
        this.idleHandler.watch();
    }

    private closeIdleHandler() {
        if (this.idleHandler) {
            this.idleHandler.stop();
            this.idleHandlerSubscriptions.unsubscribe();
        }
    }

    private initIdleHandler(): boolean {
        if (this.idleHandler.isRunning()) {
            return false; // If already running, don't init again.
        }
        this.idleHandler.setIdle(this.IDLE_ACTION_SECONDS);
        this.idleHandler.setTimeout(this.TIMEOUT_COUNTDOWN_SECONDS);
        this.idleHandler.setInterrupts([new DocumentInterruptSource('keydown mousedown')]);

        this.idleHandlerSubscriptions.add(
            this.idleHandler.onIdleStart.subscribe(() => {
                // Set absolute date/time for timeout which will be used in showing the countdown more accurately
                this.sessionTimeoutTime = DateTime.now().plus({ seconds: this.TIMEOUT_COUNTDOWN_SECONDS });

                this.pendoService.trackEvent({
                    Type: 'IdleWarning',
                    Body: {
                        AppName: 'ControlCentreApp'
                    }
                });

                console.log(`In idle status. The session will timeout in ${this.TIMEOUT_COUNTDOWN_SECONDS} seconds`);
            })
        );

        this.idleHandlerSubscriptions.add(
            this.idleHandler.onIdleEnd.subscribe(() => {
                this.ngZone.run(() => {
                    this.idleTimeoutCountdownSeconds = 0;
                });
                console.log('Not idle');
                this.startIdleHandler();
            })
        );

        this.idleHandlerSubscriptions.add(
            this.idleHandler.onTimeout.subscribe(() => {
                console.log('Timed out!');
                this.ngZone.run(() => {
                    this.idleTimeoutCountdownSeconds = 0;
                });
                this.idleLogout();
            })
        );

        this.idleHandlerSubscriptions.add(
            this.idleHandler.onTimeoutWarning.subscribe(countdown => {
                this.ngZone.run(() => {
                    // To avoid the drift we first try to use absolute date/time (this.sessionTimeoutTime)
                    // to calculate the countdown
                    this.idleTimeoutCountdownSeconds = this.sessionTimeoutTime
                        ? Math.max(Math.round(this.sessionTimeoutTime.diffNow().as('seconds')), 0)
                        : countdown;
                    this.idleTimeoutCountdownString = Duration.fromMillis(this.idleTimeoutCountdownSeconds * 1000).toFormat("m'm 's's'");
                    if (this.idleTimeoutCountdownSeconds <= 0) {
                        // idleHandler.onTimeout doesn't fire if the Chrome tab is inactive for some time
                        // It only fires when user clicks on the page after inactivity. We need to manually
                        // log user out to work around that.
                        this.closeIdleHandler();
                        this.idleLogout();
                    }
                });
                console.log('Session will time out in ' + this.idleTimeoutCountdownSeconds + ' seconds!');
            })
        );

        return true;
    }

    // should this be in its own service?
    private configureGoogleAnalytics(router: Router): boolean {
        // write GA script out to page. Remainder of code is in index.html
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://www.googletagmanager.com/gtag/js?id=' + ConfigService.settings.googleAnalytics.id;
        const head = document.getElementsByTagName('head')[0];
        head.insertBefore(script, head.firstChild);

        // call GA gtag function on NavigationEnd
        const navEndEvent$ = router.events.pipe(filter(e => e instanceof NavigationEnd));
        navEndEvent$.subscribe((e: NavigationEnd) => {
            gtag('config', ConfigService.settings.googleAnalytics.id, { page_path: e.urlAfterRedirects });
        });

        return true; // in case we need to report errors, return false
    }

    private subscribeOnlineStatus() {
        const that = this;
        this.subscriptions.add(
            this.onlineStatusHandler.offlineEvent.subscribe(event => {
                that.snackBarService.openSnackBar(Constants.InternetConnectionLostMessage, SnackbarService.SNACK_BAR_CLASS_ALERT, null);
            })
        );

        this.subscriptions.add(
            this.onlineStatusHandler.onlineEvent.subscribe(event => {
                that.snackBarService.openSnackBar(Constants.InternetConnectionRestoredMessage, SnackbarService.SNACK_BAR_CLASS_SUCCESS);
            })
        );
    }
}
